import React from 'react';
import PropTypes from 'prop-types';

// =============================================================================
// PageTitle — v2 React primitive for the `title` slot of `PageHeaderLayout`.
//
// Composes:
//   · optional `status` slot (typically a `<StatusTag>` / `<StatusPill>`),
//     rendered at the left anchor
//   · h1 with v2 typography + responsive font ramp + single-line ellipsize
//
// Layout contract — see SPECIFICATION.md §10:
//   · `flex-wrap: nowrap` — title never drops to a second line; long names
//     ellipsize via overflow rules.
//   · `align-items: baseline` — pill + title text baselines align.
//   · Pill BEFORE title (status-first scan order) — matches v1 titleIcon
//     convention and the campaigns-list table.
//   · Responsive font ramp: 22 px (≥ md) → 18 px (md) → 16 px (sm), via
//     `.page-header__title`'s token-driven media queries.
//
// Usage:
//   <PageHeaderLayout
//     title={
//       <PageTitle
//         title="Coca-Cola Summer 2026"
//         status={<StatusTag {...statusProps} />}
//       />
//     }
//   />
//
// Passed as a React node to `PageHeaderLayout.title` so the shared
// component (15+ callers) isn't forked. Other routes keep the legacy
// `<PageTitleText>` string branch until their own facelift.
// =============================================================================

const PageTitle = ({ title, status, description, className }) => {
  const extraClass = className ? ` ${className}` : '';
  const titleNode = (
    <h1
      className="page-header__title"
      title={typeof title === 'string' ? title : undefined}
    >
      {title}
    </h1>
  );
  // Without description: bare title row (status + h1 in flex-row) — same
  // DOM shape as before so existing consumers don't reflow.
  if (!description) {
    return (
      <div className={`page-header__title-row${extraClass}`}>
        {status}
        {titleNode}
      </div>
    );
  }
  // With description: status sits on the left; the title + description are
  // a flex-column on its right so the description aligns with the title's
  // left edge (not under the pill). `align-items: flex-start` on the row
  // keeps the pill anchored to the title's first line.
  return (
    <div className={`page-header__title-row page-header__title-row--with-description${extraClass}`}>
      {status}
      <div className="page-header__title-text-stack">
        {titleNode}
        <div className="page-header__title-description">{description}</div>
      </div>
    </div>
  );
};

PageTitle.propTypes = {
  title: PropTypes.node.isRequired,
  status: PropTypes.node,
  description: PropTypes.node,
  className: PropTypes.string,
};

export default PageTitle;
